// MUTUAL-AID INTEROPERABILITY — track incoming/outgoing aid between agencies.
import React, { useCallback, useEffect, useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import { Handshake, ArrowDownLeft, ArrowUpRight } from "lucide-react";

const inp = "bg-[#0d0e11] border border-[#2C2D35] px-2.5 py-2 text-xs font-mono text-white outline-none focus:border-[#3B82F6] rounded-sm";
const MA_COLOR = { requested: "#F59E0B", approved: "#3B82F6", declined: "#6B7280", active: "#22C55E", released: "#9CA3AF" };
const MA_NEXT = { requested: ["approved", "declined"], approved: ["active", "released"], active: ["released"], declined: [], released: [] };

export function MutualAidBoard() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ direction: "incoming", agency: "", contact: "", reason: "", resources: "" });
  const load = useCallback(() => client.get("/command/mutual-aid").then((r) => setRows(r.data.mutual_aid || [])).catch(() => {}), []);
  useEffect(() => { load(); const iv = setInterval(load, 15000); return () => clearInterval(iv); }, [load]);

  const create = async () => {
    if (!form.agency.trim()) { toast.error("Agency required"); return; }
    try {
      await client.post("/command/mutual-aid", form);
      toast.success("Mutual-aid request logged");
      setForm({ direction: "incoming", agency: "", contact: "", reason: "", resources: "" });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not log mutual aid"); }
  };
  const setStatus = async (r, status) => {
    try { await client.patch(`/command/mutual-aid/${r.aid_id}`, { status }); toast.success(`${r.aid_id} → ${status.toUpperCase()}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update"); }
  };
  const addNote = async (r) => {
    const text = window.prompt(`Add note to ${r.aid_id} (${r.agency}):`);
    if (!text?.trim()) return;
    try { await client.patch(`/command/mutual-aid/${r.aid_id}`, { note: text.trim() }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not add note"); }
  };

  return (
    <div className="space-y-3" data-testid="mutual-aid-board">
      <div className="border border-[#2C2D35] rounded-sm p-3 space-y-2" data-testid="ma-create-form">
        <div className="font-head text-xs font-semibold flex items-center gap-2"><Handshake size={13} className="text-[#22D3EE]" /> LOG MUTUAL-AID REQUEST</div>
        <div className="flex gap-2 flex-wrap">
          <select data-testid="ma-direction-select" className={inp} value={form.direction} onChange={(e) => setForm({ ...form, direction: e.target.value })}>
            <option value="incoming">INCOMING (they help us)</option>
            <option value="outgoing">OUTGOING (we help them)</option>
          </select>
          <input data-testid="ma-agency-input" className={`${inp} w-64`} placeholder="Agency (e.g. Allegheny County Sheriff)" value={form.agency} onChange={(e) => setForm({ ...form, agency: e.target.value })} />
          <input data-testid="ma-contact-input" className={`${inp} w-52`} placeholder="Contact (name / phone / channel)" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} />
          <input data-testid="ma-reason-input" className={`${inp} flex-1 min-w-[200px]`} placeholder="Reason / mission" value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
          <input data-testid="ma-resources-input" className={`${inp} flex-1 min-w-[200px]`} placeholder="Resources (e.g. 6 deputies, 3 marked cars, 1 K9)" value={form.resources} onChange={(e) => setForm({ ...form, resources: e.target.value })} />
          <button data-testid="ma-create-btn" onClick={create} className="px-4 py-2 rounded-sm font-head font-bold text-xs text-black" style={{ background: "#22D3EE" }}>LOG REQUEST</button>
        </div>
      </div>
      <div className="border border-[#2C2D35] rounded-sm" data-testid="ma-list">
        <div className="px-3 py-2 border-b border-[#2C2D35] bg-[#0d0e11] font-head text-xs font-semibold">MUTUAL-AID BOARD ({rows.length})</div>
        {rows.length === 0 && <div className="p-3 font-mono text-[11px] text-[#6B7280]">No mutual-aid activity logged.</div>}
        {rows.map((r) => (
          <div key={r.aid_id} className="px-3 py-2.5 border-b border-[#1E1F24]" data-testid={`ma-row-${r.aid_id}`}>
            <div className="flex items-center gap-2 flex-wrap">
              {r.direction === "incoming" ? <ArrowDownLeft size={14} className="text-[#22C55E]" /> : <ArrowUpRight size={14} className="text-[#F59E0B]" />}
              <span className="font-mono text-[10px] text-[#22D3EE]">{r.aid_id}</span>
              <span className="text-white font-semibold text-xs">{r.agency}</span>
              <span className="font-head text-[9px] font-bold px-1.5 py-0.5 rounded" style={{ background: MA_COLOR[r.status] || "#6B7280", color: "#0A0A0A" }}>{(r.status || "").toUpperCase()}</span>
              <span className="font-mono text-[9px] text-[#6B7280]">{(r.created_at || "").slice(0, 16).replace("T", " ")} · {r.by}</span>
              <span className="ml-auto flex gap-1.5">
                {(MA_NEXT[r.status] || []).map((s) => (
                  <button key={s} data-testid={`ma-status-${r.aid_id}-${s}`} onClick={() => setStatus(r, s)}
                    className="px-2 py-1 text-[9px] font-head font-bold rounded" style={{ background: MA_COLOR[s], color: "#0A0A0A" }}>
                    {s.toUpperCase()}
                  </button>
                ))}
                <button data-testid={`ma-note-${r.aid_id}`} onClick={() => addNote(r)} className="px-2 py-1 text-[9px] font-head font-bold rounded border border-[#2C2D35] text-[#9CA3AF]">+ NOTE</button>
              </span>
            </div>
            <div className="font-mono text-[10px] text-[#9CA3AF] mt-1">
              {r.reason && <>MISSION: {r.reason} · </>}{r.resources && <>RESOURCES: {r.resources} · </>}{r.contact && <>CONTACT: {r.contact}</>}
            </div>
            {(r.notes || []).slice(-3).map((n, i) => (
              <div key={i} className="font-mono text-[9px] text-[#6B7280] mt-0.5">↳ {(n.ts || "").slice(11, 16)} {n.by}: {n.text}</div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
